import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config';
import type {
  DeleteWebhookEndpointParams,
  GetRecentWebhookDeliveryParams,
  RecentWebhookDeliveryType,
  RotateWebhookSecretParams,
  RotateWebhookSecretResponse,
  SaveWebhookEndpointParams,
  SaveWebhookEndpointResponse,
  SendWebhookTestParams,
  SendWebhookTestResponse,
  WebhookEndpointType,
} from '../../../../types/webhook';
import { WebhookEventType } from '../../../../constants/enum';

/**
 * Webhook 設定 Callable のフロント側ラッパー
 * spec §5.3 / §7.3 / §12-2
 */

interface RawCallableEndpoint {
  endpoint_id: string;
  company_id: string;
  pattern_id: string;
  url: string;
  enabled: boolean;
  event_type: WebhookEventType;
  secret_last4: string;
  status: number;
  last_delivery_at?: { _seconds?: number; seconds?: number } | string;
  last_delivery_status?: number;
  last_delivery_http_status?: number;
  created_at: { _seconds?: number; seconds?: number };
  created_by: string;
  updated_at: { _seconds?: number; seconds?: number };
  updated_by: string;
}

/**
 * httpsCallable 経由で受け取った Timestamp 互換 オブジェクトを Date に変換する。
 */
const toDate = (
  raw: { _seconds?: number; seconds?: number } | string | undefined,
): Date | undefined => {
  if (!raw) return undefined;
  if (typeof raw === 'string') {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  const seconds = raw._seconds ?? raw.seconds;
  if (typeof seconds !== 'number') return undefined;
  return new Date(seconds * 1000);
};

const fromRawEndpoint = (raw: RawCallableEndpoint): WebhookEndpointType => ({
  endpoint_id: raw.endpoint_id,
  company_id: raw.company_id,
  pattern_id: raw.pattern_id,
  url: raw.url,
  enabled: raw.enabled,
  event_type: raw.event_type,
  secret_last4: raw.secret_last4,
  status: raw.status,
  last_delivery_at: toDate(raw.last_delivery_at),
  last_delivery_status: raw.last_delivery_status,
  last_delivery_http_status: raw.last_delivery_http_status,
  created_at: toDate(raw.created_at) ?? new Date(0),
  created_by: raw.created_by,
  updated_at: toDate(raw.updated_at) ?? new Date(0),
  updated_by: raw.updated_by,
});

interface SaveResponseRaw {
  success: boolean;
  endpoint: RawCallableEndpoint | null;
  plaintext_secret: string | null;
}

export const saveWebhookEndpoint = async (
  params: SaveWebhookEndpointParams,
): Promise<SaveWebhookEndpointResponse> => {
  const callable = httpsCallable<SaveWebhookEndpointParams, SaveResponseRaw>(
    functions,
    'saveWebhookEndpoint',
  );
  const result = await callable(params);
  if (!result.data.endpoint) {
    throw new Error('エンドポイント情報を取得できませんでした。');
  }
  return {
    endpoint: fromRawEndpoint(result.data.endpoint),
    plaintext_secret: result.data.plaintext_secret,
  };
};

export const deleteWebhookEndpoint = async (params: DeleteWebhookEndpointParams): Promise<void> => {
  const callable = httpsCallable<DeleteWebhookEndpointParams, { success: boolean }>(
    functions,
    'deleteWebhookEndpoint',
  );
  await callable(params);
};

interface RotateResponseRaw {
  success: boolean;
  endpoint: RawCallableEndpoint | null;
  plaintext_secret: string;
}

export const rotateWebhookSecret = async (
  params: RotateWebhookSecretParams,
): Promise<RotateWebhookSecretResponse> => {
  const callable = httpsCallable<RotateWebhookSecretParams, RotateResponseRaw>(
    functions,
    'rotateWebhookSecret',
  );
  const result = await callable(params);
  if (!result.data.endpoint) {
    throw new Error('エンドポイント情報を取得できませんでした。');
  }
  return {
    endpoint: fromRawEndpoint(result.data.endpoint),
    plaintext_secret: result.data.plaintext_secret,
  };
};

interface SendWebhookTestResponseRaw {
  success: boolean;
  http_status: number | null;
  response_body_excerpt: string | null;
  latency_ms: number;
  error_reason: string | null;
  event_id: string;
}

export const sendWebhookTest = async (
  params: SendWebhookTestParams,
): Promise<SendWebhookTestResponse> => {
  const callable = httpsCallable<SendWebhookTestParams, SendWebhookTestResponseRaw>(
    functions,
    'sendWebhookTest',
  );
  const result = await callable(params);
  return {
    http_status: result.data.http_status,
    response_body_excerpt: result.data.response_body_excerpt,
    latency_ms: result.data.latency_ms,
    error_reason: result.data.error_reason,
    event_id: result.data.event_id,
  };
};

interface RecentDeliveryResponseRaw {
  success: boolean;
  delivery: {
    delivery_id: string;
    event_id: string;
    event_type: WebhookEventType;
    status: number;
    http_status: number | null;
    error_reason: string | null;
    latency_ms: number | null;
    dispatched_at: string | null;
    created_at: string | null;
  } | null;
}

export const getRecentWebhookDelivery = async (
  params: GetRecentWebhookDeliveryParams,
): Promise<RecentWebhookDeliveryType | null> => {
  const callable = httpsCallable<GetRecentWebhookDeliveryParams, RecentDeliveryResponseRaw>(
    functions,
    'getRecentWebhookDelivery',
  );
  const result = await callable(params);
  const d = result.data.delivery;
  if (!d) return null;
  return {
    delivery_id: d.delivery_id,
    event_id: d.event_id,
    event_type: d.event_type,
    status: d.status,
    http_status: d.http_status,
    error_reason: d.error_reason,
    latency_ms: d.latency_ms,
    dispatched_at: d.dispatched_at ? new Date(d.dispatched_at) : undefined,
    created_at: d.created_at ? new Date(d.created_at) : new Date(0),
  };
};
